import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { ServiceRequest, PaginatedResponse, getPhotoSrc } from '@/lib/api'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import DataTable from '@/components/DataTable'
import AdvancedFilters from '@/components/AdvancedFilters'
import FilteredEmptyState from '@/components/FilteredEmptyState'
import SlideOver from '@/components/SlideOver'
import { Plus, Search, AlertCircle, Clock, CheckCircle, Upload, X, FileText, DollarSign, Image, Building2, Edit3, UserCheck, Bell, Loader2, FileDown } from 'lucide-react'
import UserSearchSelect from '@/components/UserSearchSelect'
import CommentsSection from '@/components/CommentsSection'
import ProjectSearchSelect, { ProjectInfo } from '@/components/ProjectSearchSelect'
import AuditInfo from '@/components/AuditInfo'
import { formatMyanmarDateTime, formatDateOnly } from '@/lib/dateUtils'

const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed']
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low']
const CATEGORIES = ['Hardware Error', 'Software Error', 'System Maintenance', 'Others']

function formatDuration(createdAt: string, resolvedAt: string | null): string {
  if (!resolvedAt) return ''
  try {
    const c = new Date(createdAt)
    const r = new Date(resolvedAt)
    const ms = r.getTime() - c.getTime()
    if (ms < 0) return ''
    const days = Math.floor(ms / 86400000)
    const hours = Math.floor((ms % 86400000) / 3600000)
    const mins = Math.floor((ms % 3600000) / 60000)
    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    if (mins > 0 && days === 0) parts.push(`${mins}m`)
    return parts.join(' ') || '< 1m'
  } catch { return '' }
}

export default function ServiceRequests() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [engineerFilter, setEngineerFilter] = useState('')
  const [projectCodeFilter, setProjectCodeFilter] = useState('')
  const [billingFilter, setBillingFilter] = useState('')
  const [reportedFrom, setReportedFrom] = useState('')
  const [reportedTo, setReportedTo] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [resolvedFrom, setResolvedFrom] = useState('')
  const [resolvedTo, setResolvedTo] = useState('')
  const [expiryFrom, setExpiryFrom] = useState('')
  const [expiryTo, setExpiryTo] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<ServiceRequest | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState<any>({})
  const [error, setError] = useState('')
  const [billingForm, setBillingForm] = useState<{ type: string; amount: number }>({ type: 'FOC', amount: 0 })
  const [showBilling, setShowBilling] = useState(false)
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([])
  const [pendingSrNotify, setPendingSrNotify] = useState<{name: string, download_url: string}[]>([])
  const [isNotifyingSr, setIsNotifyingSr] = useState(false)
  const [projectPopup, setProjectPopup] = useState<any>(null)
  const [loadingProject, setLoadingProject] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) {
      api.get(`/service-requests/${openId}`).then(r => {
        setSelected(r.data)
        setEditMode(false)
        setShowBilling(false)
        setSearchParams({}, { replace: true })
      }).catch(() => setSearchParams({}, { replace: true }))
    }
  }, [])

  useEffect(() => {
    setPendingSrNotify([])
  }, [selected?.id])

  const openProjectPopup = async (projectId: number | null) => {
    if (!projectId) return
    setLoadingProject(true)
    try {
      const res = await api.get(`/projects/${projectId}`)
      setProjectPopup(res.data)
    } catch {
      setProjectPopup(null)
    }
    setLoadingProject(false)
  }

  const { data: filterUsers = [] } = useQuery<{ id: number; full_name: string; role: string }[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users/list').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const engineeringUsers = filterUsers.filter(user => ['chief_engineer', 'operation_manager', 'engineer', 'technician'].includes(user.role))
  const advancedSrParams = {
    assigned_to: engineerFilter || undefined, project_code: projectCodeFilter || undefined,
    is_billed: billingFilter === '' ? undefined : billingFilter === 'billed',
    reported_from: reportedFrom || undefined, reported_to: reportedTo || undefined,
    created_from: createdFrom || undefined, created_to: createdTo || undefined,
    resolved_from: resolvedFrom || undefined, resolved_to: resolvedTo || undefined,
    expiry_from: expiryFrom || undefined, expiry_to: expiryTo || undefined,
  }
  const advancedSrFilterCount = Object.values(advancedSrParams).filter(value => value !== undefined && value !== '').length
  const resetAdvancedSrFilters = () => {
    setEngineerFilter(''); setProjectCodeFilter(''); setBillingFilter(''); setReportedFrom(''); setReportedTo('')
    setCreatedFrom(''); setCreatedTo(''); setResolvedFrom(''); setResolvedTo(''); setExpiryFrom(''); setExpiryTo(''); setPage(1)
  }
  const resetAllSrFilters = () => {
    setSearch(''); setStatusFilter(''); setPriorityFilter(''); setCategoryFilter(''); resetAdvancedSrFilters()
  }
  useEffect(() => {
    setPage(1)
  }, [engineerFilter, projectCodeFilter, billingFilter, reportedFrom, reportedTo, createdFrom, createdTo, resolvedFrom, resolvedTo, expiryFrom, expiryTo])

  const { data, isLoading } = useQuery<PaginatedResponse<ServiceRequest>>({
    queryKey: ['srs', search, statusFilter, priorityFilter, categoryFilter, advancedSrParams, page],
    queryFn: () => api.get('/service-requests', { params: { search, status: statusFilter, priority: priorityFilter, category: categoryFilter, ...advancedSrParams, page, per_page: 25 } }).then(r => r.data),
  })

  const { data: metricsData } = useQuery({
    queryKey: ['sr-metrics'],
    queryFn: async () => {
      const [open, inProgress, resolved] = await Promise.all([
        api.get('/service-requests', { params: { status: 'Open', per_page: 1 } }),
        api.get('/service-requests', { params: { status: 'In Progress', per_page: 1 } }),
        api.get('/service-requests', { params: { status: 'Resolved', per_page: 1 } }),
      ])
      return { open: open.data.total, inProgress: inProgress.data.total, resolved: resolved.data.total }
    },
  })

  const createSR = useMutation({
    mutationFn: async (formData: any) => {
      const res = await api.post('/service-requests', formData)
      const sr = res.data
      if (pendingPhotos.length > 0) {
        const failedPhotos: string[] = []
        for (const file of pendingPhotos) {
          try {
            const fd = new FormData()
            fd.append('file', file)
            await api.post(`/service-requests/${sr.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
          } catch {
            failedPhotos.push(file.name)
          }
        }
        if (failedPhotos.length > 0 && failedPhotos.length === pendingPhotos.length) {
          try {
            await api.delete(`/service-requests/${sr.id}`)
            throw new Error(`All photo uploads failed (${failedPhotos.join(', ')}). Service request was not created.`)
          } catch (delErr: any) {
            if (delErr?.message?.includes('photo uploads failed')) throw delErr
            throw new Error(`All photo uploads failed. Service request ${sr.sr_number} was created without photos.`)
          }
        } else if (failedPhotos.length > 0) {
          return { ...sr, _photoWarning: `Created, but ${failedPhotos.length} photo(s) failed to upload: ${failedPhotos.join(', ')}` }
        }
      }
      return sr
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['srs'] }); queryClient.invalidateQueries({ queryKey: ['sr-metrics'] }); setShowCreate(false); setForm({}); setPendingPhotos([])
      if (data?._photoWarning) { setError(data._photoWarning) } else { setError('') }
    },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to create service request') },
  })

  const updateSR = useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/service-requests/${id}`, data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['srs'] }); queryClient.invalidateQueries({ queryKey: ['sr-metrics'] }); setSelected(res.data); setEditMode(false); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to update service request') },
  })

  const deleteSR = useMutation({
    mutationFn: (id: number) => api.delete(`/service-requests/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['srs'] }); queryClient.invalidateQueries({ queryKey: ['sr-metrics'] }); setSelected(null); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to delete service request') },
  })

  const billSR = useMutation({
    mutationFn: ({ id, billing_type, billing_amount }: { id: number; billing_type: string; billing_amount: number }) =>
      api.post(`/service-requests/${id}/bill?billing_type=${billing_type}&billing_amount=${billing_amount}`),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['srs'] }); setSelected(res.data); setShowBilling(false); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to bill service request') },
  })

  const uploadPhoto = useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      return api.post(`/service-requests/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['srs'] }); if (selected) { api.get(`/service-requests/${selected.id}`).then(r => setSelected(r.data)) } },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to upload photo') },
  })

  const deletePhoto = useMutation({
    mutationFn: ({ id, filename }: { id: number; filename: string }) =>
      api.delete(`/service-requests/${id}/photos/${encodeURIComponent(filename || '')}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['srs'] }); if (selected) { api.get(`/service-requests/${selected.id}`).then(r => setSelected(r.data)) } },
  })

  const quickStatus = (newStatus: string) => {
    if (selected) updateSR.mutate({ id: selected.id, status: newStatus })
  }

  const handleProjectSelect = (project: ProjectInfo) => {
    setForm({
      ...form,
      project_id: project.id,
      project_code: `${project.project_type}-${project.project_code}`,
      client_name: project.client_name,
      contact_number: project.phone || '',
      address: (project as any).address || '',
      service_expiry_date: project.service_expiry_date || '',
    })
  }

  const handleProjectClear = () => {
    setForm({
      ...form,
      project_id: null,
      project_code: '',
      client_name: '',
      contact_number: '',
      address: '',
      service_expiry_date: '',
    })
  }

  const handleCreatePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPendingPhotos([...pendingPhotos, ...Array.from(e.target.files)])
    }
  }

  const handleDetailPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !selected) return
    const files = Array.from(e.target.files)
    e.target.value = ''
    const newPending: {name: string, download_url: string}[] = []
    for (const f of files) {
      try {
        const res = await uploadPhoto.mutateAsync({ id: selected.id, file: f })
        const fileMeta = res.data?.file
        if (fileMeta?.download_url) {
          newPending.push({ name: fileMeta.name || f.name, download_url: fileMeta.download_url })
        }
      } catch {}
    }
    if (newPending.length > 0) {
      setPendingSrNotify(prev => [...prev, ...newPending])
    }
  }

  const handleSrNotify = async () => {
    if (!selected || pendingSrNotify.length === 0) return
    setIsNotifyingSr(true)
    try {
      const res = await api.post(`/service-requests/${selected.id}/photos/batch-notify`, {
        download_urls: pendingSrNotify.map(p => p.download_url).filter(Boolean),
        names: pendingSrNotify.map(p => p.name),
      })
      if (res.data?.notification_sent !== false) {
        setPendingSrNotify([])
      } else {
        setError('Notification could not be delivered. Please try again.')
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send notification')
    } finally {
      setIsNotifyingSr(false)
    }
  }

  const photos = selected?.photos ? (typeof selected.photos === 'string' ? (() => { try { return JSON.parse(selected.photos) } catch { return [] } })() : selected.photos) : []

  const columns = [
    { key: 'sr_number', label: 'SR#', render: (s: ServiceRequest) => <span className="font-medium text-red-800">{s.sr_number}</span> },
    { key: 'project_code', label: 'Project', render: (s: ServiceRequest) => <span className="text-xs text-slate-600">{s.project_code || '—'}</span> },
    { key: 'client_name', label: 'Client', render: (s: ServiceRequest) => <span className="font-medium text-slate-900">{s.client_name}</span> },
    { key: 'category', label: 'Category' },
    { key: 'priority', label: 'Priority', render: (s: ServiceRequest) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.priority === 'Critical' ? 'bg-red-100 text-red-700' : s.priority === 'High' ? 'bg-orange-100 text-orange-700' : s.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{s.priority}</span>
    )},
    { key: 'assigned_to', label: 'Assigned' },
    { key: 'reported_date', label: 'Date', render: (s: ServiceRequest) => s.reported_date || '—' },
    { key: 'status', label: 'Status', render: (s: ServiceRequest) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'Open' ? 'bg-red-100 text-red-900' : s.status === 'In Progress' ? 'bg-purple-100 text-purple-700' : s.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{s.status}</span>
    )},
  ]

  const metrics = [
    { label: 'Open', value: metricsData?.open ?? 0, icon: AlertCircle, color: 'text-red-800', bg: 'bg-red-50' },
    { label: 'In Progress', value: metricsData?.inProgress ?? 0, icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Resolved', value: metricsData?.resolved ?? 0, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  ]

  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = user.role || ''
  const isAdminRole = userRole === 'super_admin' || userRole === 'admin'
  const isFinance = userRole === 'finance'
  const isHR = userRole === 'hr'
  const isEngineer = userRole === 'engineer' || userRole === 'technician' || userRole === 'procurement' || userRole === 'storekeeper'
  const { canDo } = useMyPermissions()
  const canCreateSR = canDo('sr_ss_create')
  const canEditSR = (sr: ServiceRequest) => {
    if (isAdminRole || canDo('sr_ss_edit_all')) return true
    if (isFinance || isHR) return false
    if (isEngineer) return sr.assigned_to === user.full_name || sr.created_by === user.id
    return true
  }
  const canBill = canDo('billing')
  const canDeleteSR = isAdminRole

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Service Requests</h1>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            try {
              const res = await api.get('/export/service-requests', { params: { status: statusFilter, priority: priorityFilter, category: categoryFilter, search, ...advancedSrParams }, responseType: 'blob' })
              const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = 'service_requests.xlsx'; a.click(); URL.revokeObjectURL(url)
            } catch {}
          }} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600" title="Export to Excel">
            <FileDown size={15} /> <span className="hidden sm:inline">Export</span>
          </button>
          {canCreateSR && (
            <button onClick={() => { setShowCreate(true); setForm({ status: 'Open', priority: 'Medium', category: 'Others' }); setPendingPhotos([]); setError('') }}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 min-h-[44px]">
              <Plus size={16} /> New Service
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {metrics.map(m => (
          <div key={m.label} className={`${m.bg} rounded-xl p-4 border border-slate-200`}>
            <div className="flex items-center gap-3">
              <m.icon className={m.color} size={20} />
              <div>
                <div className="text-2xl font-bold text-slate-900">{m.value}</div>
                <div className="text-xs text-slate-500">{m.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input type="text" placeholder="Search SRs..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Priority</option>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Category</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <AdvancedFilters activeCount={advancedSrFilterCount} onReset={resetAdvancedSrFilters} onApply={() => setPage(1)}>
            <label className="text-xs font-medium text-slate-600">Site engineer
              <select value={engineerFilter} onChange={e => setEngineerFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All site engineers</option>{engineeringUsers.map(user => <option key={user.id} value={user.full_name}>{user.full_name}</option>)}</select>
            </label>
            <label className="text-xs font-medium text-slate-600">Project code
              <input value={projectCodeFilter} onChange={e => setProjectCodeFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" placeholder="e.g. CLAPJ-001" />
            </label>
            <label className="text-xs font-medium text-slate-600">Billing state
              <select value={billingFilter} onChange={e => setBillingFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All billing states</option><option value="billed">Billed</option><option value="unbilled">Not billed</option></select>
            </label>
            <label className="text-xs font-medium text-slate-600">Reported from<input type="date" value={reportedFrom} onChange={e => setReportedFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Reported to<input type="date" value={reportedTo} onChange={e => setReportedTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Created from<input type="date" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Created to<input type="date" value={createdTo} onChange={e => setCreatedTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Resolved from<input type="date" value={resolvedFrom} onChange={e => setResolvedFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Resolved to<input type="date" value={resolvedTo} onChange={e => setResolvedTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Service expiry from<input type="date" value={expiryFrom} onChange={e => setExpiryFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Service expiry to<input type="date" value={expiryTo} onChange={e => setExpiryTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
          </AdvancedFilters>
        </div>
        <DataTable columns={columns} data={data?.data || []} total={data?.total || 0}
          page={page} totalPages={data?.total_pages || 1} onPageChange={setPage}
          onRowClick={s => { setSelected(s); setEditMode(false); setShowBilling(false); setPendingSrNotify([]) }} selectedId={selected?.id}
          getRowId={s => s.id} loading={isLoading} />
        <FilteredEmptyState show={!isLoading && (data?.total || 0) === 0 && !!(search || statusFilter || priorityFilter || categoryFilter || advancedSrFilterCount)} onReset={resetAllSrFilters} />
      </div>

      {/* Detail / Edit SlideOver */}
      <SlideOver open={!!selected} onClose={() => { setSelected(null); setEditMode(false); setShowBilling(false); setError(''); setPendingSrNotify([]) }} title={selected?.sr_number || ''} wide>
        {selected && !editMode && (
          <div className="space-y-5">
            {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
            <div className="flex flex-wrap gap-2">
              {selected.status === 'Open' && <button onClick={() => quickStatus('In Progress')} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Start Work</button>}
              {(selected.status === 'Open' || selected.status === 'In Progress') && <button onClick={() => quickStatus('Resolved')} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Resolve</button>}
              {selected.status === 'Resolved' && <button onClick={() => quickStatus('Closed')} className="px-3 py-1.5 text-sm bg-slate-600 text-white rounded-lg hover:bg-slate-700">Close</button>}
              {(selected.status === 'Resolved' || selected.status === 'Closed') && <button onClick={() => quickStatus('Open')} className="px-3 py-1.5 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Reopen</button>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm"><span className="text-xs text-slate-500 block">Project Code</span>
                {selected.project_code ? (
                  userRole === 'technician' || userRole === 'hr'
                    ? <span className="font-medium text-slate-900">{selected.project_code}</span>
                    : <button type="button" onClick={() => openProjectPopup(selected.project_id)} className="font-medium text-red-800 hover:underline text-left">{selected.project_code}</button>
                ) : <span className="font-medium text-slate-900">—</span>}
              </div>
              {[['Client', selected.client_name], ['Contact Number', selected.contact_number], ['Address', selected.address], ['Category', selected.category], ['Priority', selected.priority], ['Status', selected.status], ['Assigned To', selected.assigned_to], ['Reported Date', formatDateOnly(selected.reported_date)], ['Service Expiry', formatDateOnly(selected.service_expiry_date)]].map(([l, v]) => (
                <div key={l as string} className="text-sm"><span className="text-xs text-slate-500 block">{l}</span><span className="font-medium text-slate-900">{v || '—'}</span></div>
              ))}
              {selected.resolved_at && (
                <div className="text-sm"><span className="text-xs text-slate-500 block">Duration</span>
                  <span className="font-medium text-green-700">{formatDuration(selected.created_at, selected.resolved_at)}</span>
                </div>
              )}
            </div>

            {selected.description && <div className="text-sm"><span className="text-xs text-slate-500 block">Description</span><p className="text-slate-700 mt-1">{selected.description}</p></div>}
            {selected.resolution_notes && <div className="text-sm"><span className="text-xs text-slate-500 block">Resolution Notes</span><p className="text-slate-700 mt-1 bg-green-50 p-3 rounded-lg">{selected.resolution_notes}</p></div>}

            <AuditInfo
              createdByName={selected.created_by_name}
              createdAt={selected.created_at}
              updatedAt={selected.updated_at}
            />

            {/* Photos */}
            {photos.length > 0 && (
              <div>
                <span className="text-xs text-slate-500 block mb-2">Photos ({photos.length})</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {photos.map((p: any, i: number) => {
                    const photoSrc = getPhotoSrc(p);
                    const photoName = typeof p === 'string' ? `photo-${i}` : (p.name || `photo-${i}`);
                    const photoId = typeof p === 'string' ? null : (p.id || p.name);
                    return (
                    <div key={i} className="relative group">
                      <a href={photoSrc} target="_blank" rel="noopener noreferrer">
                        <img src={photoSrc} alt={photoName} className="w-full h-24 object-cover rounded-lg border" />
                      </a>
                      {photoId && (
                      <button onClick={() => deletePhoto.mutate({ id: selected.id, filename: photoId })}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity min-w-[28px] min-h-[28px] flex items-center justify-center">
                        <X size={14} />
                      </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <label className={`flex items-center gap-1.5 text-sm text-red-800 hover:text-red-900 cursor-pointer ${uploadPhoto.isPending ? 'opacity-60 pointer-events-none' : ''}`}>
                {uploadPhoto.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Add Photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleDetailPhotoUpload} disabled={uploadPhoto.isPending} />
              </label>
              {pendingSrNotify.length > 0 && (
                <button type="button" onClick={handleSrNotify} disabled={isNotifyingSr}
                  className="flex items-center gap-1.5 text-xs bg-green-700 text-white px-2.5 py-1 rounded-lg hover:bg-green-800 disabled:opacity-50">
                  {isNotifyingSr ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
                  Notify Team ({pendingSrNotify.length})
                </button>
              )}
            </div>

            {/* Billing Section */}
            {canBill && (selected.status === 'Resolved' || selected.status === 'Closed') && !selected.is_billed && (
              <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Billing Required</span>
                </div>
                {!showBilling ? (
                  <button onClick={() => { setShowBilling(true); setBillingForm({ type: 'FOC', amount: 0 }) }}
                    className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700">Set Billing</button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setBillingForm({ ...billingForm, type: 'FOC', amount: 0 })}
                        className={`px-3 py-1.5 text-sm rounded-lg ${billingForm.type === 'FOC' ? 'bg-green-600 text-white' : 'bg-white border text-slate-600'}`}>FOC</button>
                      <button type="button" onClick={() => setBillingForm({ ...billingForm, type: 'Collected' })}
                        className={`px-3 py-1.5 text-sm rounded-lg ${billingForm.type === 'Collected' ? 'bg-red-800 text-white' : 'bg-white border text-slate-600'}`}>Collected</button>
                    </div>
                    {billingForm.type === 'Collected' && (
                      <input type="number" min="0" step="0.01" placeholder="Amount" value={billingForm.amount || ''}
                        onChange={e => setBillingForm({ ...billingForm, amount: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 text-sm border rounded-lg" />
                    )}
                    <div className="flex gap-2">
                      <button onClick={() => billSR.mutate({ id: selected.id, billing_type: billingForm.type, billing_amount: billingForm.amount })}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Confirm Billing</button>
                      <button onClick={() => setShowBilling(false)} className="px-3 py-1.5 text-sm bg-slate-100 rounded-lg">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selected.is_billed && (
              <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-green-600 block">Billing</span>
                    <span className="font-medium text-green-800">
                      {selected.billing_type === 'FOC' ? 'Free of Charge (FOC)' : `Collected: ${Number(selected.billing_amount).toLocaleString()} MMK`}
                    </span>
                  </div>
                  <button onClick={async () => {
                    try {
                      const res = await api.get(`/service-requests/${selected.id}/receipt`, { responseType: 'blob' })
                      const blob = new Blob([res.data], { type: 'application/pdf' })
                      const url = URL.createObjectURL(blob)
                      window.open(url, '_blank')
                    } catch (err: any) {
                      setError(err.response?.data?.detail || 'Failed to generate receipt')
                    }
                  }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
                    <FileText size={14} /> Print Receipt
                  </button>
                </div>
              </div>
            )}

            <SRActivityTrail srId={selected.id} />

            <CommentsSection entityType="service_request" entityId={selected.id} />

            {(canEditSR(selected) || canDeleteSR) && (
              <div className="flex gap-2 pt-3 border-t">
                {canEditSR(selected) && <button onClick={() => { setEditMode(true); setForm(selected) }} className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Edit</button>}
                {canDeleteSR && (!selected.is_billed || isAdminRole) && (
                  <button onClick={() => {
                    if (selected.is_billed) { if (confirm('This SR has been billed. Are you sure you want to delete it?')) deleteSR.mutate(selected.id) }
                    else { if (confirm('Delete?')) deleteSR.mutate(selected.id) }
                  }} className="py-2 px-4 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100">Delete</button>
                )}
              </div>
            )}
          </div>
        )}
        {selected && editMode && (
          <form onSubmit={e => { e.preventDefault(); const { id, ...rest } = form; updateSR.mutate({ id: selected.id, ...rest }) }} className="space-y-3">
            {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}

            <div className="text-sm"><span className="text-xs text-slate-500 block mb-1">Project Code</span>
              <span className="font-medium text-slate-600 bg-slate-50 px-3 py-2 rounded-lg block">{selected.project_code || '—'}</span>
            </div>

            <div><label className="text-sm font-medium text-slate-700 block mb-1">Client Name</label>
              <input value={form.client_name || ''} onChange={e => setForm({ ...form, client_name: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Contact Number</label>
              <input value={form.contact_number || ''} onChange={e => setForm({ ...form, contact_number: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Address</label>
              <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
            <UserSearchSelect label="Assigned To" value={form.assigned_to || ''} onChange={v => setForm({ ...form, assigned_to: v })} />
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Category</label>
              <select value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Priority</label>
              <select value={form.priority || ''} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Status</label>
              <select value={form.status || ''} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                {STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
              <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Resolution Notes</label>
              <textarea value={form.resolution_notes || ''} onChange={e => setForm({ ...form, resolution_notes: e.target.value })} rows={3} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div className="flex gap-2">
              <button type="submit" className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Save</button>
              <button type="button" onClick={() => setEditMode(false)} className="py-2 px-4 text-sm bg-slate-100 rounded-lg">Cancel</button>
            </div>
          </form>
        )}
      </SlideOver>

      {/* Create SlideOver */}
      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); setPendingPhotos([]); setError('') }} title="New Service Request">
        <form onSubmit={e => { e.preventDefault(); createSR.mutate(form) }} className="space-y-3">
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}

          <ProjectSearchSelect
            value={form.project_code || ''}
            onSelect={handleProjectSelect}
            onClear={handleProjectClear}
          />

          {form.project_id && (
            <div className="grid grid-cols-2 gap-2 bg-red-50 p-3 rounded-lg text-sm">
              <div><span className="text-xs text-red-700 block">Client</span><span className="font-medium">{form.client_name}</span></div>
              <div><span className="text-xs text-red-700 block">Service Expiry</span><span className="font-medium">{form.service_expiry_date || '—'}</span></div>
              {form.address && <div className="col-span-2"><span className="text-xs text-red-700 block">Address</span><span className="font-medium">{form.address}</span></div>}
            </div>
          )}

          <div><label className="text-sm font-medium text-slate-700 block mb-1">Contact Number</label>
            <input value={form.contact_number || ''} onChange={e => setForm({ ...form, contact_number: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" placeholder="Phone number" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Category</label>
            <select value={form.category || 'Others'} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Priority</label>
            <select value={form.priority || 'Medium'} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></div>
          <UserSearchSelect label="Assigned To" value={form.assigned_to || ''} onChange={v => setForm({ ...form, assigned_to: v })} />
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Photos</label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
              <label className="flex items-center gap-2 mx-auto text-sm text-red-800 hover:text-red-900 cursor-pointer">
                <Image size={16} /> Add Photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleCreatePhotoAdd} />
              </label>
              {pendingPhotos.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {pendingPhotos.map((f, i) => (
                    <div key={i} className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded text-xs">
                      <span>{f.name}</span>
                      <button type="button" onClick={() => setPendingPhotos(pendingPhotos.filter((_, j) => j !== i))} className="text-red-500"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button type="submit" disabled={createSR.isPending} className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
            {createSR.isPending ? 'Creating...' : 'Create Service Request'}
          </button>
        </form>
      </SlideOver>

      {(projectPopup || loadingProject) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setProjectPopup(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-red-800" />
                <h3 className="text-lg font-bold text-slate-900">
                  {projectPopup ? `${projectPopup.project_type}-${projectPopup.project_code}` : 'Loading...'}
                </h3>
              </div>
              <button onClick={() => setProjectPopup(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            {loadingProject && !projectPopup && (
              <div className="p-8 text-center text-slate-500">Loading project information...</div>
            )}
            {projectPopup && (
              <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  {(() => {
                    const assigned = projectPopup.assigned_to
                    let teamLeader = ''
                    let salesPerson = ''
                    if (typeof assigned === 'string') {
                      try {
                        const parsed = JSON.parse(assigned)
                        teamLeader = parsed.team_leader || ''
                        salesPerson = parsed.sales_person || ''
                      } catch {
                        teamLeader = assigned
                      }
                    } else if (assigned && typeof assigned === 'object') {
                      teamLeader = assigned.team_leader || ''
                      salesPerson = assigned.sales_person || ''
                    }
                    const fields = [
                      ['Project Code', `${projectPopup.project_type}-${projectPopup.project_code}`],
                      ['Client', projectPopup.client_name],
                      ['Phone', projectPopup.phone],
                      ['Team Leader', teamLeader],
                      ['Sales Person', salesPerson],
                      ['Date Completed', projectPopup.date_completed],
                      ['Expiry Date', projectPopup.service_expiry_date],
                    ]
                    return fields.map(([label, value]) => (
                      <div key={label as string} className="text-sm">
                        <span className="text-xs text-slate-500 block">{label}</span>
                        <span className="font-medium text-slate-900">{value || '—'}</span>
                      </div>
                    ))
                  })()}
                </div>
                {projectPopup.address && (
                  <div className="text-sm">
                    <span className="text-xs text-slate-500 block">Address</span>
                    <span className="font-medium text-slate-900">{projectPopup.address}</span>
                  </div>
                )}
                {projectPopup.description && (
                  <div className="text-sm">
                    <span className="text-xs text-slate-500 block">Description</span>
                    <p className="font-medium text-slate-900 mt-1">{projectPopup.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function SRActivityTrail({ srId }: { srId: number }) {
  const { data: activities } = useQuery<any[]>({
    queryKey: ['sr-activities', srId],
    queryFn: () => api.get(`/service-requests/${srId}/activities`).then(r => r.data),
    enabled: !!srId,
  })

  if (!activities || activities.length === 0) return null

  const getIcon = (type: string) => {
    if (type.includes('Created')) return <Plus size={12} className="text-green-600" />
    if (type.includes('Status')) return <UserCheck size={12} className="text-blue-600" />
    if (type.includes('Billed')) return <DollarSign size={12} className="text-amber-600" />
    return <Edit3 size={12} className="text-slate-500" />
  }

  return (
    <div className="text-sm">
      <span className="text-xs text-slate-500 font-medium block mb-2">Activity Trail</span>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {activities.map((a: any) => (
          <div key={a.id} className="flex items-start gap-2 text-xs bg-slate-50 px-3 py-2 rounded-lg">
            <div className="mt-0.5">{getIcon(a.activity_type)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-700">{a.subject}</div>
              <div className="text-slate-400">{a.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
